<template>
  <div class="inventory-request-form">
    <FormValidation
      v-model="formData"
      :rules="validationRules"
      :show-validation-summary="true"
      :validate-on-change="true"
      @validation-change="onValidationChange"
      ref="formValidator"
    >
      <template #default="{ errors, hasError, setFieldValue, getFieldValue }">
        <v-form ref="vForm" @submit.prevent="handleSubmit">
          <!-- Header del formulario -->
          <div class="form-header mb-6">
            <div class="d-flex align-center mb-4">
              <v-icon size="32" color="primary" class="mr-3">
                mdi-package-variant
              </v-icon>
              <div>
                <h2 class="text-h5 font-weight-bold">
                  {{ isEdit ? 'Editar Solicitud' : 'Nueva Solicitud de Inventario' }}
                </h2>
                <p class="text-body-2 text-grey mb-0">
                  {{ isEdit ? 'Modifica los detalles de la solicitud' : 'Solicita productos o materiales para tu agencia' }}
                </p>
              </div>
            </div>
            
            <!-- Wizard steps -->
            <v-stepper 
              v-model="currentStep" 
              :items="steps"
              hide-actions
              class="elevation-0"
            ></v-stepper>
          </div>

          <!-- Step 1: Información básica -->
          <v-window v-model="currentStep">
            <v-window-item :value="1">
              <v-card class="mb-4" elevation="2">
                <v-card-title class="bg-primary text-white">
                  <v-icon class="mr-2">mdi-information</v-icon>
                  Información de la Solicitud
                </v-card-title>
                <v-card-text class="pa-6">
                  <v-row>
                    <!-- Agencia solicitante -->
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        :model-value="getFieldValue('agencyId')"
                        @update:model-value="(val) => { setFieldValue('agencyId', val); onAgencyChange(val); }"
                        :items="agencyOptions"
                        label="Agencia solicitante *"
                        placeholder="Selecciona tu agencia"
                        variant="outlined"
                        :error="hasError('agencyId')"
                        :error-messages="errors.agencyId"
                        prepend-inner-icon="mdi-store"
                        :loading="loadingAgencies"
                        @blur="markTouched('agencyId')"
                      >
                        <template #item="{ props, item }">
                          <v-list-item v-bind="props">
                            <template #title>
                              <div class="d-flex justify-space-between align-center">
                                <span>{{ item.title }}</span>
                                <v-chip size="x-small" variant="outlined">
                                  {{ item.raw.code }}
                                </v-chip>
                              </div>
                            </template>
                            <template #subtitle>
                              <span class="text-caption">{{ item.raw.address }}</span>
                            </template>
                          </v-list-item>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <!-- Tipo de solicitud -->
                    <v-col cols="12" md="6">
                      <v-select
                        :model-value="getFieldValue('requestType')"
                        @update:model-value="(val) => setFieldValue('requestType', val)"
                        :items="requestTypeOptions"
                        label="Tipo de solicitud *"
                        variant="outlined"
                        :error="hasError('requestType')"
                        :error-messages="errors.requestType"
                        prepend-inner-icon="mdi-format-list-bulleted-type"
                        @blur="markTouched('requestType')"
                      >
                        <template #item="{ props, item }">
                          <v-list-item v-bind="props" :title="item.title">
                            <template #prepend>
                              <v-icon :color="item.raw.color">
                                {{ item.raw.icon }}
                              </v-icon>
                            </template>
                            <template #subtitle>
                              <span class="text-caption">{{ item.raw.description }}</span>
                            </template>
                          </v-list-item>
                        </template>
                      </v-select>
                    </v-col>

                    <!-- Prioridad -->
                    <v-col cols="12" md="6">
                      <v-select
                        :model-value="getFieldValue('priority')"
                        @update:model-value="(val) => setFieldValue('priority', val)"
                        :items="priorityOptions"
                        label="Prioridad *"
                        variant="outlined"
                        :error="hasError('priority')"
                        :error-messages="errors.priority"
                        prepend-inner-icon="mdi-priority-high"
                        @blur="markTouched('priority')"
                      >
                        <template #item="{ props, item }">
                          <v-list-item v-bind="props" :title="item.title">
                            <template #prepend>
                              <v-chip
                                :color="item.raw.color"
                                size="small"
                                variant="flat"
                              >
                                {{ item.title }}
                              </v-chip>
                            </template>
                          </v-list-item>
                        </template>
                      </v-select>
                    </v-col>

                    <!-- Fecha requerida -->
                    <v-col cols="12" md="6">
                      <v-text-field
                        :model-value="getFieldValue('requiredDate')"
                        @update:model-value="(val) => setFieldValue('requiredDate', val)"
                        label="Fecha requerida *"
                        type="date"
                        variant="outlined"
                        :error="hasError('requiredDate')"
                        :error-messages="errors.requiredDate"
                        prepend-inner-icon="mdi-calendar"
                        :min="minDate"
                        @blur="markTouched('requiredDate')"
                      ></v-text-field>
                    </v-col>

                    <!-- Justificación -->
                    <v-col cols="12">
                      <v-textarea
                        :model-value="getFieldValue('justification')"
                        @update:model-value="(val) => setFieldValue('justification', val)"
                        label="Justificación de la solicitud *"
                        placeholder="Explica por qué necesitas estos productos..."
                        variant="outlined"
                        :error="hasError('justification')"
                        :error-messages="errors.justification"
                        rows="3"
                        counter="300"
                        maxlength="300"
                        @blur="markTouched('justification')"
                      ></v-textarea>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-window-item>

            <!-- Step 2: Selección de productos -->
            <v-window-item :value="2">
              <v-card class="mb-4" elevation="2">
                <v-card-title class="bg-success text-white">
                  <v-icon class="mr-2">mdi-cart</v-icon>
                  Selección de Productos
                </v-card-title>
                <v-card-text class="pa-6">
                  <!-- Buscador de productos -->
                  <div class="product-search mb-6">
                    <v-row>
                      <v-col cols="12" md="8">
                        <v-autocomplete
                          v-model="selectedProduct"
                          :items="filteredProducts"
                          label="Buscar productos"
                          placeholder="Busca por código, nombre o categoría..."
                          variant="outlined"
                          prepend-inner-icon="mdi-magnify"
                          :loading="loadingProducts"
                          clearable
                          @update:search="onProductSearch"
                          item-title="name"
                          item-value="id"
                          return-object
                        >
                          <template #item="{ props, item }">
                            <v-list-item v-bind="props">
                              <template #prepend>
                                <v-avatar size="40">
                                  <v-img
                                    v-if="item.raw.image"
                                    :src="item.raw.image"
                                    :alt="item.raw.name"
                                  ></v-img>
                                  <v-icon v-else>mdi-package</v-icon>
                                </v-avatar>
                              </template>
                              <template #title>
                                <div class="d-flex justify-space-between align-center">
                                  <span>{{ item.raw.name }}</span>
                                  <v-chip size="x-small" color="primary" variant="outlined">
                                    {{ item.raw.code }}
                                  </v-chip>
                                </div>
                              </template>
                              <template #subtitle>
                                <div class="d-flex align-center">
                                  <span class="text-caption mr-2">{{ item.raw.category }}</span>
                                  <v-divider vertical class="mx-2"></v-divider>
                                  <span class="text-caption">
                                    Stock: {{ item.raw.stock }} {{ item.raw.unit }}
                                  </span>
                                </div>
                              </template>
                            </v-list-item>
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-btn
                          block
                          color="primary"
                          size="large"
                          :disabled="!selectedProduct"
                          @click="addProductToRequest"
                        >
                          <v-icon class="mr-2">mdi-plus</v-icon>
                          Agregar Producto
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Lista de productos seleccionados -->
                  <div v-if="requestedProducts.length > 0">
                    <v-divider class="mb-4"></v-divider>
                    <h4 class="text-h6 mb-4">Productos solicitados:</h4>
                    
                    <v-row>
                      <v-col
                        v-for="(product, index) in requestedProducts"
                        :key="index"
                        cols="12"
                      >
                        <v-card variant="outlined" class="product-item">
                          <v-card-text class="pa-4">
                            <v-row align="center">
                              <!-- Imagen del producto -->
                              <v-col cols="auto">
                                <v-avatar size="60">
                                  <v-img
                                    v-if="product.image"
                                    :src="product.image"
                                    :alt="product.name"
                                  ></v-img>
                                  <v-icon v-else size="30">mdi-package</v-icon>
                                </v-avatar>
                              </v-col>

                              <!-- Información del producto -->
                              <v-col>
                                <div class="d-flex align-center mb-2">
                                  <h5 class="text-subtitle-1 font-weight-medium mr-2">
                                    {{ product.name }}
                                  </h5>
                                  <v-chip size="small" color="primary" variant="outlined">
                                    {{ product.code }}
                                  </v-chip>
                                </div>
                                <p class="text-body-2 text-grey mb-1">
                                  Categoría: {{ product.category }}
                                </p>
                                <p class="text-body-2 text-grey mb-0">
                                  Stock disponible: {{ product.stock }} {{ product.unit }}
                                </p>
                              </v-col>

                              <!-- Cantidad solicitada -->
                              <v-col cols="auto">
                                <div class="quantity-control">
                                  <v-text-field
                                    v-model.number="product.requestedQuantity"
                                    label="Cantidad"
                                    type="number"
                                    variant="outlined"
                                    style="width: 120px"
                                    :min="1"
                                    :max="product.stock"
                                    :suffix="product.unit"
                                    hide-details
                                    @input="validateQuantity(product)"
                                  ></v-text-field>
                                </div>
                              </v-col>

                              <!-- Acciones -->
                              <v-col cols="auto">
                                <v-btn
                                  icon="mdi-delete"
                                  color="error"
                                  variant="text"
                                  @click="removeProduct(index)"
                                ></v-btn>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Estado vacío -->
                  <div v-else class="text-center py-8">
                    <v-icon size="64" color="grey-lighten-1">mdi-cart-outline</v-icon>
                    <p class="text-h6 text-grey mt-4">No hay productos seleccionados</p>
                    <p class="text-body-2 text-grey">
                      Usa el buscador arriba para agregar productos a tu solicitud
                    </p>
                  </div>
                </v-card-text>
              </v-card>
            </v-window-item>

            <!-- Step 3: Revisión final -->
            <v-window-item :value="3">
              <v-card class="mb-4" elevation="2">
                <v-card-title class="bg-info text-white">
                  <v-icon class="mr-2">mdi-eye</v-icon>
                  Revisión de la Solicitud
                </v-card-title>
                <v-card-text class="pa-6">
                  <!-- Resumen de información -->
                  <div class="request-summary">
                    <v-row>
                      <v-col cols="12" md="6">
                        <h4 class="text-subtitle-1 font-weight-bold mb-3">
                          Información General
                        </h4>
                        <div class="summary-item">
                          <strong>Agencia:</strong> {{ getAgencyName(getFieldValue('agencyId')) }}
                        </div>
                        <div class="summary-item">
                          <strong>Tipo:</strong> {{ getRequestTypeName(getFieldValue('requestType')) }}
                        </div>
                        <div class="summary-item">
                          <strong>Prioridad:</strong>
                          <v-chip 
                            :color="getPriorityColor(getFieldValue('priority'))"
                            size="small"
                            variant="flat"
                          >
                            {{ getPriorityName(getFieldValue('priority')) }}
                          </v-chip>
                        </div>
                        <div class="summary-item">
                          <strong>Fecha requerida:</strong> {{ formatDate(getFieldValue('requiredDate')) }}
                        </div>
                      </v-col>
                      
                      <v-col cols="12" md="6">
                        <h4 class="text-subtitle-1 font-weight-bold mb-3">
                          Justificación
                        </h4>
                        <p class="text-body-2">{{ getFieldValue('justification') }}</p>
                      </v-col>
                    </v-row>
                  </div>

                  <v-divider class="my-6"></v-divider>

                  <!-- Resumen de productos -->
                  <div class="products-summary">
                    <h4 class="text-subtitle-1 font-weight-bold mb-4">
                      Productos Solicitados ({{ requestedProducts.length }})
                    </h4>
                    
                    <v-simple-table>
                      <template #default>
                        <thead>
                          <tr>
                            <th>Producto</th>
                            <th>Código</th>
                            <th>Cantidad</th>
                            <th>Unidad</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="product in requestedProducts" :key="product.id">
                            <td>{{ product.name }}</td>
                            <td>
                              <v-chip size="small" variant="outlined">
                                {{ product.code }}
                              </v-chip>
                            </td>
                            <td>{{ product.requestedQuantity }}</td>
                            <td>{{ product.unit }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </div>

                  <!-- Observaciones adicionales -->
                  <div class="additional-notes mt-6">
                    <v-textarea
                      :model-value="getFieldValue('additionalNotes')"
                      @update:model-value="(val) => setFieldValue('additionalNotes', val)"
                      label="Observaciones adicionales (opcional)"
                      placeholder="Agrega cualquier información adicional relevante..."
                      variant="outlined"
                      rows="3"
                      counter="200"
                      maxlength="200"
                    ></v-textarea>
                  </div>
                </v-card-text>
              </v-card>
            </v-window-item>
          </v-window>

          <!-- Navegación del stepper -->
          <div class="stepper-actions mt-6">
            <v-row>
              <v-col cols="12" class="d-flex justify-space-between">
                <v-btn
                  variant="outlined"
                  :disabled="currentStep === 1"
                  @click="previousStep"
                >
                  <v-icon class="mr-2">mdi-chevron-left</v-icon>
                  Anterior
                </v-btn>

                <div class="step-indicator">
                  <span class="text-body-2 text-grey">
                    Paso {{ currentStep }} de {{ steps.length }}
                  </span>
                </div>

                <div>
                  <v-btn
                    v-if="currentStep < steps.length"
                    color="primary"
                    @click="nextStep"
                    :disabled="!canProceedToNextStep"
                  >
                    Siguiente
                    <v-icon class="ml-2">mdi-chevron-right</v-icon>
                  </v-btn>

                  <v-btn
                    v-else
                    color="success"
                    @click="handleSubmit"
                    :disabled="!isFormValid || isSubmitting || requestedProducts.length === 0"
                    :loading="isSubmitting"
                  >
                    <v-icon class="mr-2">mdi-send</v-icon>
                    {{ isEdit ? 'Actualizar Solicitud' : 'Enviar Solicitud' }}
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-form>
      </template>
    </FormValidation>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import FormValidation from './FormValidation.vue'

interface InventoryRequestFormData {
  agencyId: string
  requestType: string
  priority: string
  requiredDate: string
  justification: string
  additionalNotes?: string
  products: Array<{
    id: string
    name: string
    code: string
    category: string
    unit: string
    stock: number
    requestedQuantity: number
    image?: string
  }>
}

interface InventoryRequestFormProps {
  modelValue?: InventoryRequestFormData
  isEdit?: boolean
  agencies?: Array<any>
  products?: Array<any>
}

interface InventoryRequestFormEmits {
  (e: 'update:modelValue', value: InventoryRequestFormData): void
  (e: 'submit', data: InventoryRequestFormData): void
  (e: 'cancel'): void
}

const props = withDefaults(defineProps<InventoryRequestFormProps>(), {
  modelValue: () => ({
    agencyId: '',
    requestType: '',
    priority: '',
    requiredDate: '',
    justification: '',
    products: []
  }),
  isEdit: false,
  agencies: () => [],
  products: () => []
})

const emit = defineEmits<InventoryRequestFormEmits>()

// Referencias
const formValidator = ref()
const vForm = ref()

// Estado reactivo
const formData = ref<InventoryRequestFormData>({ ...props.modelValue })
const isFormValid = ref(false)
const isSubmitting = ref(false)
const currentStep = ref(1)
const selectedProduct = ref(null)
const requestedProducts = ref([])
const loadingAgencies = ref(false)
const loadingProducts = ref(false)
const filteredProducts = ref([])

// Configuración del stepper
const steps = [
  { title: 'Información', value: 1 },
  { title: 'Productos', value: 2 },
  { title: 'Revisión', value: 3 }
]

// Opciones de datos
const requestTypeOptions = [
  {
    title: 'Reposición',
    value: 'RESTOCK',
    icon: 'mdi-refresh',
    color: 'blue',
    description: 'Reponer productos agotados'
  },
  {
    title: 'Nueva agencia',
    value: 'NEW_AGENCY',
    icon: 'mdi-store-plus',
    color: 'green',
    description: 'Equipar nueva agencia'
  },
  {
    title: 'Emergencia',
    value: 'EMERGENCY',
    icon: 'mdi-alert',
    color: 'red',
    description: 'Solicitud urgente'
  },
  {
    title: 'Mejoras',
    value: 'UPGRADE',
    icon: 'mdi-trending-up',
    color: 'purple',
    description: 'Mejoras de equipamiento'
  }
]

const priorityOptions = [
  { title: 'Urgente', value: 'URGENT', color: 'error' },
  { title: 'Alta', value: 'HIGH', color: 'warning' },
  { title: 'Media', value: 'MEDIUM', color: 'info' },
  { title: 'Baja', value: 'LOW', color: 'success' }
]

// Reglas de validación
const validationRules = {
  agencyId: [
    { required: true, message: 'La agencia es requerida' }
  ],
  requestType: [
    { required: true, message: 'El tipo de solicitud es requerido' }
  ],
  priority: [
    { required: true, message: 'La prioridad es requerida' }
  ],
  requiredDate: [
    { required: true, message: 'La fecha requerida es obligatoria' }
  ],
  justification: [
    { required: true, message: 'La justificación es requerida' },
    { minLength: 20, message: 'Mínimo 20 caracteres para la justificación' }
  ]
}

// Computed
const agencyOptions = computed(() => {
  return props.agencies.map(agency => ({
    title: `${agency.name} - ${agency.code}`,
    value: agency.id,
    code: agency.code,
    address: agency.address
  }))
})

const minDate = computed(() => {
  const today = new Date()
  return today.toISOString().split('T')[0]
})

const canProceedToNextStep = computed(() => {
  switch (currentStep.value) {
    case 1:
      return !!(
        formData.value.agencyId &&
        formData.value.requestType &&
        formData.value.priority &&
        formData.value.requiredDate &&
        formData.value.justification
      )
    case 2:
      return requestedProducts.value.length > 0
    default:
      return true
  }
})

// Lifecycle
onMounted(() => {
  filteredProducts.value = props.products
  requestedProducts.value = [...formData.value.products]
})

// Watchers
watch(() => props.modelValue, (newValue) => {
  formData.value = { ...newValue }
  requestedProducts.value = [...newValue.products]
}, { deep: true })

watch(requestedProducts, (newProducts) => {
  formData.value.products = newProducts
  emit('update:modelValue', formData.value)
}, { deep: true })

// Métodos
const onValidationChange = (valid: boolean) => {
  isFormValid.value = valid
}

const markTouched = (field: string) => {
  formValidator.value?.markFieldAsTouched(field)
}

const onAgencyChange = (agencyId: string) => {
  // Aquí se podría cargar información específica de la agencia
  console.log('Agencia seleccionada:', agencyId)
}

const onProductSearch = (search: string) => {
  if (!search) {
    filteredProducts.value = props.products
    return
  }
  
  filteredProducts.value = props.products.filter(product =>
    product.name.toLowerCase().includes(search.toLowerCase()) ||
    product.code.toLowerCase().includes(search.toLowerCase()) ||
    product.category.toLowerCase().includes(search.toLowerCase())
  )
}

const addProductToRequest = () => {
  if (!selectedProduct.value) return
  
  // Verificar si el producto ya está en la lista
  const existingProduct = requestedProducts.value.find(
    p => p.id === selectedProduct.value.id
  )
  
  if (existingProduct) {
    alert('Este producto ya está en la solicitud')
    return
  }
  
  // Agregar producto con cantidad inicial de 1
  requestedProducts.value.push({
    ...selectedProduct.value,
    requestedQuantity: 1
  })
  
  selectedProduct.value = null
}

const removeProduct = (index: number) => {
  requestedProducts.value.splice(index, 1)
}

const validateQuantity = (product: any) => {
  if (product.requestedQuantity < 1) {
    product.requestedQuantity = 1
  } else if (product.requestedQuantity > product.stock) {
    product.requestedQuantity = product.stock
  }
}

const nextStep = () => {
  if (currentStep.value < steps.length) {
    currentStep.value++
  }
}

const previousStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--
  }
}

const getAgencyName = (agencyId: string) => {
  const agency = props.agencies.find(a => a.id === agencyId)
  return agency ? `${agency.name} - ${agency.code}` : ''
}

const getRequestTypeName = (type: string) => {
  const requestType = requestTypeOptions.find(t => t.value === type)
  return requestType ? requestType.title : ''
}

const getPriorityName = (priority: string) => {
  const priorityOption = priorityOptions.find(p => p.value === priority)
  return priorityOption ? priorityOption.title : ''
}

const getPriorityColor = (priority: string) => {
  const priorityOption = priorityOptions.find(p => p.value === priority)
  return priorityOption ? priorityOption.color : 'grey'
}

const formatDate = (dateString: string) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const handleSubmit = async () => {
  const isValid = await formValidator.value.validate()
  
  if (!isValid || requestedProducts.value.length === 0) {
    return
  }
  
  isSubmitting.value = true
  
  try {
    emit('submit', { ...formData.value })
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.inventory-request-form {
  max-width: 1000px;
  margin: 0 auto;
}

.form-header {
  text-align: center;
}

.product-item {
  transition: all 0.3s ease;
}

.product-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.quantity-control {
  min-width: 120px;
}

.summary-item {
  margin-bottom: 8px;
  line-height: 1.5;
}

.stepper-actions {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding-top: 24px;
}

.step-indicator {
  display: flex;
  align-items: center;
}

.v-stepper {
  box-shadow: none !important;
}
</style>
